import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Modal } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { decode, encode } from 'html-entities';
import { boolean } from 'boolean';

import { useAppSelector } from 'hooks';
import { Dropdown } from 'components/Forms';
import { DateTimePicker, TextArea } from 'components/UI';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { buildObservationsOptions } from 'utils/MeasurementsStoreFunctions';
import { getObservationCollections } from 'store/groups/reducer';

import GenerateActionEngine from '../../../GenerateActionEngine';
import RecurrenceEngine from '../../../RecurrenceEngine';

interface IAddObservationsModalProps {
  show: boolean;
  handleClose: any;
  handleAdd: any;
  observation?: ICarePlanObservationTask;
  carePlanTemplate?: ICarePlanTemplate;
}

const AddEditObservationModal = ({
  show,
  handleClose,
  handleAdd,
  observation,
  carePlanTemplate
}: IAddObservationsModalProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);
  const patientUid = useAppSelector(({ patient }) => patient.data?.uid);
  const observationCollections = useAppSelector(getObservationCollections);
  const observationsToSelect: ISelectOption[] = buildObservationsOptions(observationCollections);

  const [observationSelected, setObservationSelected] = useState(observation?.mClass || '');
  const [notificationContent, setNotificationContent] = useState(
    observation?.action.survey?.messageText || intl.formatMessage({ id: 'timeToTakeMeasurement' })
  );

  const now = DateTime.now().setLocale(locale);
  const observationDate =
    observation?.send_time && typeof observation?.send_time !== 'number'
      ? DateTime.fromFormat(observation.send_time, 'yyyy-MM-dd HH:mm:ss')
      : undefined;
  const [date, setDate] = useState<DateTime>(observationDate || now.plus({ hours: 1 }));

  const [actionsOnTask, setActionsOnTask] = useState<ICarePlanActionOnTask[]>(
    observation?.action?.survey?.actions_on_task || []
  );
  const [recurrence, setRecurrence] = useState(
    observation?.isRepeating ? observation.recurrence : ''
  );
  const [validRecurrence, setValidRecurrence] = useState(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);

  const modalTitle = observation ? 'editObservationKey' : 'addObservationKey';
  const submitButtonLabel = observation ? 'saveButtonTitle' : 'add';

  const [{ loading, error: postError }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patientUid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const _handleAdd = async () => {
    const _observation = observationCollections.find(
      (e) => e.measurementClass === observationSelected
    );
    if (!_observation) return;

    const observationToCreate = {
      edit_status: observation ? 'updated' : 'new',
      id: observation?.id,
      action: {
        survey: {
          messageText: notificationContent,
          linkText: _observation.title,
          surveyId: [_observation.surveyId],
          actions_on_task: actionsOnTask
        },
        localDateTime: date.toFormat('yyyy-MM-dd HH:mm')
      },
      recurrence
    };

    await post({
      data: {
        scheduled: [observationToCreate],
        carePlanTemplate
      }
    });

    handleAdd();
  };

  const _handleClose = () => {
    handleClose(false);
  };

  const onAddActions = (generatedActions: ICarePlanActionOnTask[]) => {
    setActionsOnTask(generatedActions);
  };

  const onChangeRecurrence = (_recurrence: string, _valid: boolean) => {
    setRecurrence(_recurrence);
    setValidRecurrence(_valid);
  };

  useEffect(() => {
    const isObservationIdentical =
      observationSelected === observation?.mClass &&
      date.toMillis() === observationDate?.toMillis() &&
      actionsOnTask === observation?.action?.survey?.actions_on_task &&
      recurrence === (observation?.isRepeating ? observation.recurrence : '');
    setAddButtonDisabled(!observationSelected || !validRecurrence || isObservationIdentical);
  }, [
    observation,
    validRecurrence,
    observationSelected,
    date,
    actionsOnTask,
    recurrence,
    observationDate
  ]);

  return (
    <Modal
      data-test="add-edit-observation-modal"
      show={show}
      onHide={_handleClose}
      backdrop="static"
    >
      <Modal.Header className="align-items-center" closeButton>
        <Modal.Title data-test="add-edit-observation-title" data-test-value={modalTitle}>
          {intl.formatMessage({ id: modalTitle })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Dropdown
          dataTestId="add-edit-observation-class-select"
          variant="form"
          label={intl.formatMessage({ id: 'observationKey' })}
          options={observationsToSelect}
          value={observationsToSelect.find((elem) => elem.value === observationSelected)}
          onChange={({ value }: ISelectOption) => setObservationSelected(value)}
        />

        <TextArea
          dataTestId="add-edit-observation-message"
          value={decode(notificationContent)}
          onChange={(e) => setNotificationContent(encode(e.target.value))}
          label={intl.formatMessage({ id: 'messageKey' })}
          rows={3}
          containerClassName="mt-4"
        />

        <div className="d-flex flex-column mt-4">
          <DateTimePicker
            dataTestPrefix="add-edit-observation"
            date={date}
            todayLimit="min"
            onChangeDate={setDate}
            minDate={now}
          />
        </div>

        <RecurrenceEngine
          recurrence={recurrence}
          onChange={onChangeRecurrence}
          isActive={boolean(observation?.isRepeating)}
          date={date}
        />

        <GenerateActionEngine onAddActions={onAddActions} currentActions={actionsOnTask} />

        {loading && <Loading className="mt-4" />}
        {postError && <Error className="mt-4" retry={_handleAdd} dismissible={false} />}
      </Modal.Body>
      <Modal.Footer>
        <Button data-test="add-edit-observation-cancel-btn" variant="link" onClick={_handleClose}>
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          data-test="add-edit-observation-submit-btn"
          data-test-value={submitButtonLabel}
          variant="primary"
          onClick={_handleAdd}
          disabled={addButtonDisabled}
        >
          {intl.formatMessage({ id: submitButtonLabel })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditObservationModal;
